/* eslint-disable no-use-before-define */
/* eslint-disable import/no-unresolved */
/* eslint-disable no-unused-vars */
import PropTypes from 'prop-types';
import { useContext, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
// @mui
import { styled, alpha } from '@mui/material/styles';
import {
  Box, Link, Button, Drawer, Typography, Avatar, Stack, Divider, Chip, Tooltip, IconButton, List, ListItemButton, ListItemIcon, FormControlLabel, Switch, useTheme,
} from '@mui/material';
// mock
import account from '../../../_mock/account';
// hooks
import useResponsive from '../../../hooks/useResponsive';
// components
import Logo from '../../../components/logo';
import Scrollbar from '../../../components/scrollbar';
import NavSection from '../../../components/nav-section';
import {
  Business, EditNotifications, Logout, ManageAccounts, Settings, Terminal, Menu, History, LocationSearching, BatteryUnknown,
} from '@mui/icons-material';
import SvgColor from '../../../components/svg-color';
import { AppContext, useApp } from 'src/utils/AppContext';
import { usesTagsFeature, isPlatformSuperAdmin } from 'src/utils/orgFeatures';
import { useFerreroOverviewAccess } from 'src/api/ferrero';
import { AuthContext, useAuth } from 'src/utils/AuthContext';
import { isBulkReportsEnabledOrg } from 'src/utils/bulkReports';
import { Icon } from '@iconify/react';
import { FEATURES } from 'src/config';

// ----------------------------------------------------------------------

export const NAV_WIDTH = 260;
export const NAV_WIDTH_CLOSED = 50;

// ----------------------------------------------------------------------

Nav.propTypes = {
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
};

const ASSETTYPE_TO_EL = {
  asset: (config = { assetName: 'Asset' }) => ({
    title: `Anchor | ${config.assetName}s`,
    path: '/assets',
    icon: <Icon icon="maki:warehouse" height="24px" />,
  }),
  shipment: (config = { assetName: 'Shipment' }) => ({
    title: `Go | ${config.assetName}s`,
    path: '/shipments',
    icon: <Icon icon="solar:delivery-bold-duotone" height="24px" />,
    nest: [
      {
        title: `Active ${config.assetName}s`,
        path: '/shipments',
        icon: <LocationSearching />,
        end: true,
      },
      {
        title: `Past ${config.assetName}s`,
        path: '/shipments/past',
        icon: <History />,
      },
    ],
  }),
};

// Matches LIFECELL_ORG_ID in backend/bl/webhooks.ts.
const LIFECELL_ORG_ID = 'lifecell';

const navConfigBase = [
  {
    title: 'logger inventory',
    path: 'loggers',
    icon: <Icon icon="icon-park-twotone:devices" height="24px" />,
  },
  // Hidden where user management is unavailable — on-premise, where granting the box Cognito's
  // pool-wide admin permissions would let it enumerate, create and delete users belonging to every
  // other customer, since those permissions are scoped to the pool and not to one organisation.
  // See FEATURES in src/config.js. The ROUTE is gated in routes.jsx as well: hiding a menu item does
  // not make a page unreachable, and reaching this one rendered the backend's refusal as a red error
  // banner that read like a fault.
  ...(FEATURES.users ? [{
    title: 'user management',
    path: 'usermanagement',
    icon: <Icon icon="carbon:group-access" height="24px" />,
  }] : []),
  {
    title: 'Lite Plus',
    path: 'lite',
    icon: <Icon icon="game-icons:usb-key" height="24px" />,
  },
  {
    title: 'Go Mini',
    path: 'gomini',
    icon: <Icon icon="mdi:usb-flash-drive" height="24px" />,
  },
  {
    title: 'settings',
    icon: <Settings />,
    path: 'settings',
    nest: [
      {
        title: 'profile',
        path: '/settings/profile',
        icon: <ManageAccounts />,
      },
      {
        title: 'Integrations',
        path: '/settings/integrations',
        icon: <Icon icon="icon-park-twotone:key-one" height="24px" />, // previously data-user
      },
      {
        title: 'organization',
        path: '/settings/organization',
        icon: <Business />,
      },
    ],
  },
];

export default function Nav({ openNav, setOpenNav, onCloseNav }) {
  const { authStatus } = useAuth();
  const {
    id: orgId, name: orgName, assetsConfig, mode, settings, orgs, setupOrganization, distributorOrgId,
  } = useApp();
  const theme = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { sessionInfo, signOut } = useContext(AuthContext);
  const isSuperUser = isPlatformSuperAdmin(sessionInfo?.idToken?.payload?.email) || (sessionInfo?.idToken?.payload?.['cognito:groups']?.some((g) => g.startsWith(orgId) && g.endsWith('Superuser')) ?? false);
  const { data: canSeeFerreroOverview = false } = useFerreroOverviewAccess(orgId);
  const cobrandingLogoUrl = settings?.cobranding?.logo ?? null;
  const cobrandingLogoIcon = settings?.cobranding?.icon ?? null;
  const email = (authStatus === 'SignedIn' || authStatus == 'Iframe') && sessionInfo.idToken.payload.email;
  const userName = (authStatus === 'SignedIn' || authStatus == 'Iframe') && sessionInfo.idToken.payload.name;

  const navConfig = useMemo(() => {
    const nC = [...navConfigBase];
    nC[0] = { ...nC[0] };
    if (settings?.loggerName) {
      nC[0].title = `${settings.loggerName} inventory`;
    }
    if (assetsConfig?.asset?.capa) {
      nC.splice(1, 0, {
        title: 'CAPA system',
        path: 'capa',
        icon: <Icon icon="fluent-mdl2:document-set" height="24px" />,
      });
    }
    if (settings?.hooters) {
      nC.splice(1, 0, {
        title: 'Hooters',
        path: 'hooters',
        icon: <Icon icon="ph:siren-duotone" height="24px" />,
      });
    }
    if (isBulkReportsEnabledOrg(orgId)) {
      nC.splice(2, 0, {
        title: 'downloads',
        path: 'downloads',
        icon: <Icon icon="fluent:arrow-download-24-filled" height="24px" />,
      });
    }
    // LifeCell's fixed installations read better as sensor cards than as the
    // shared inventory table. Gated to that org so no other client's nav moves.
    // Positioned relative to the inventory entry rather than at a fixed index,
    // since the splices above shift it.
    if (orgId === LIFECELL_ORG_ID) {
      const inventoryIndex = nC.findIndex((e) => e.path === 'loggers');
      nC.splice(inventoryIndex === -1 ? 0 : inventoryIndex + 1, 0, {
        title: 'sensor cards',
        path: 'devices/cards',
        icon: <Icon icon="solar:widget-4-bold-duotone" height="24px" />,
      });
    }
    if (!(authStatus === 'SignedIn' || authStatus == 'Iframe')) {
      Object.values(ASSETTYPE_TO_EL).reverse().forEach((v) => { nC.unshift(v()); });
      return nC.map((e) => ({ ...e, disabled: e.path !== 'lite' && e.path !== 'gomini' }));
    }
    (settings?.assetPriority ? [...settings.assetPriority] : Object.keys(ASSETTYPE_TO_EL)).reverse().forEach((k) => {
      if (assetsConfig[k]) nC.unshift(ASSETTYPE_TO_EL[k](assetsConfig[k]));
    });
    // Ferrero distributor overview, placed right under the distributors entry it summarises. Who may
    // see it is decided by the backend (GET /v1/ferrero/overview/access).
    if (canSeeFerreroOverview) {
      const assetsIndex = nC.findIndex((e) => e.path === '/assets');
      nC.splice(assetsIndex + 1, 0, {
        title: 'distributor overview',
        path: '/distributor-overview',
        icon: <Icon icon="mdi:thermometer-alert" height="24px" />,
      });
    }
    if (orgId !== 'Dlecta') {
      const shipmentConfigIndex = nC.findIndex((item) => item.path === '/shipments');
      if (shipmentConfigIndex !== -1 && nC[shipmentConfigIndex].nest) {
        nC[shipmentConfigIndex] = {
          ...nC[shipmentConfigIndex],
          nest: [...nC[shipmentConfigIndex].nest, {
            title: 'stops',
            path: '/shipments/stops',
            icon: <Icon icon="mdi:map-marker-radius-outline" height="24px" />,
          }],
        };
      }
    }
    /* Object.entries(ASSETTYPE_TO_EL).reverse().forEach(([k, v]) => {
      if (assetsConfig[k]) nC.unshift(v(assetsConfig[k]));
    }); */
    if (Object.keys(orgs).length > 1 || distributorOrgId) {
      nC.push({
        title: 'switch organization',
        path: '/organizations',
        onClick: () => { setupOrganization(); },
        icon: <Icon icon="icon-park:switch" height="24px" />,
      });
    }

    // The blocks below add and remove items ON the settings entry, per org. `nC` is a SHALLOW copy of
    // navConfigBase, so mutating that entry in place mutates the MODULE-LEVEL config — and the change
    // outlives the org that caused it, because switching org re-runs this memo without reloading the
    // page. That is why opening a tags org (Kims/demo1) pushed '/settings/fields' into the shared nest
    // permanently: every org opened afterwards in the same session showed a Fields menu item, whose
    // route correctly 404s for them. The same leak applied to 'periodic reports'.
    // Copy the entry and its nest first, exactly as the home (nC[0]) and shipments nodes above do.
    const settingsIndex = nC.findIndex((item) => item.title === 'settings');
    if (settingsIndex !== -1) nC[settingsIndex] = { ...nC[settingsIndex], nest: [...nC[settingsIndex].nest] };
    const settingsConfig = nC[settingsIndex];
    if (!!assetsConfig?.asset && settingsConfig?.nest[2]?.title !== 'periodic reports' && (!settings.hidePeriodicReports || isSuperUser)) {
      settingsConfig.nest.splice(2, 0, {
        title: 'periodic reports',
        path: '/settings/periodic-reports',
        icon: <Icon icon="tabler:report" height="24px" />,
      });
    } else if (!assetsConfig?.asset && settingsConfig?.nest[2]?.title === 'periodic reports') {
      settingsConfig.nest.splice(2, 1);
    }

    // Fields page is visible to every pilot-org user, but read-only for them: assigning a value to assets
    // and create/edit/delete of fields/values are superuser-only, enforced in the page and server-side.
    if (settingsConfig && usesTagsFeature(orgId) && !settingsConfig.nest.some((n) => n.path === '/settings/fields')) {
      settingsConfig.nest.push({
        title: 'fields',
        path: '/settings/fields',
        icon: <Icon icon="mdi:tag-multiple-outline" height="24px" />,
      });
    }

    nC.push({
      title: 'sign out',
      path: '/signin',
      onClick: () => { signOut(); },
      icon: <Icon icon="lets-icons:sign-out-squre" height="24px" />,
    });
    return nC;
    // orgId belongs here: several entries are org-gated (downloads, Dlecta,
    // LifeCell), and without it the nav keeps whatever it built before orgId
    // resolved.
  }, [assetsConfig, authStatus, orgId, settings, canSeeFerreroOverview]);

  const isDesktop = useResponsive('up', 'lg');

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        width: NAV_WIDTH,
        '& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{
        transition: 'width 0.1s',
        width: openNav ? NAV_WIDTH : NAV_WIDTH_CLOSED,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
      >
        {cobrandingLogoUrl && (
        <Box sx={{
          width: '100%', height: 60, display: 'flex', justifyContent: 'center', alignItems: 'center', paddingX: 0.5,
        }}
        >
          <Box
            onClick={() => { navigate('/'); }}
            component="img"
            src={cobrandingLogoUrl}
            sx={{
              position: 'absolute', maxHeight: '3.5vh', opacity: openNav ? 1 : 0, transition: openNav ? 'opacity 0.5s' : 'none', cursor: 'pointer', zIndex: 1,
            }}
          />
          <Box
            m={1}
            component="img"
            src={cobrandingLogoIcon || cobrandingLogoUrl}
            sx={{
              position: 'absolute', maxWidth: '2.1vw', opacity: openNav ? 0 : 1, transition: openNav ? 'none' : 'opacity 0.3s',
            }}
          />
        </Box>
        )}
        {!cobrandingLogoUrl && (
        <Box sx={{
          width: '100%', height: 50, display: 'flex', justifyContent: 'center', alignItems: 'center',
        }}
        >
          <Box
            component="img"
            src={orgId === 'jwllogic' ? '/assets/images/logos/Logo-01_resized.png' : orgId === 'DVIJAYPHARMA' ? '/assets/images/logos/dvijaypharma.png' : '/assets/images/logos/mindlabs-logo-dark.svg'}
            sx={{
              position: 'absolute', maxHeight: orgId === 'jwllogic' ? '4vh' : orgId === 'DVIJAYPHARMA' ? '7vh' : '2.7vh', opacity: openNav ? 1 : 0, transition: openNav ? 'opacity 0.5s' : 'none', cursor: 'pointer', zIndex: 1,
            }}
          />
          <Box
            m={1.6}
            component="img"
            src={orgId === 'jwllogic' ? '/assets/images/logos/Logo-01_resized.png' : orgId === 'DVIJAYPHARMA' ? '/assets/images/logos/dvijaypharma.png' : '/assets/images/logos/mindlabs-icon.svg'}
            sx={{
              position: 'absolute', maxHeight: orgId === 'jwllogic' ? '3vh' : '5vh', opacity: openNav ? 0 : 1, transition: openNav ? 'none' : 'opacity 0.3s',
            }}
          />
        </Box>
        )}
      </Box>

      <Divider />

      <NavSection openNav={openNav} sx={{ overflowX: 'hidden', pt: 1 }} data={navConfig} />

      {/* <Box sx={{ p: 1 }}>
        <FormControlLabel
          label="Dark mode"
          labelPlacement="start"
          control={(
            <Switch
              size="small"
              checked={theme.palette.mode === 'dark'}
              onChange={toggleTheme}
            />
          )}
        />
      </Box> */}

      <span style={{ flexGrow: 1 }} />
      {openNav && (authStatus === 'SignedIn' || authStatus == 'Iframe') && (
        <Box sx={{ padding: '1rem', width: '100%', overflow: 'hidden' }}>
          <Typography variant="subtitle1" sx={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>
            {userName}
          </Typography>
          <Typography variant="body1" sx={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>
            {orgName}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ textOverflow: 'ellipsis', overflow: 'hidden', flex: 1 }}>
              {email}
              {/* {sessionInfo.idToken.payload['cognito:groups'].includes(`${id}#Superuser`) && (
                      <Tooltip title="superuser">
                        <sup style={{ color: 'red' }}><em>su</em></sup>
                      </Tooltip>
                    )} */}
            </Typography>
            {orgId === 'stelisbiopharma' && (
              <Typography variant="caption" sx={{ color: 'text.secondary', opacity: 0.7 }}>
                v2.0
              </Typography>
            )}
          </Box>
        </Box>
      )}
      {openNav && !(authStatus === 'SignedIn' || authStatus == 'Iframe') && (
        <Box sx={{ padding: '1rem', width: '100%' }}>
          <Button fullWidth variant="contained" onClick={() => { navigate('/signin'); }}>
            Sign In
          </Button>
        </Box>
      )}
      {cobrandingLogoUrl && (authStatus === 'SignedIn' || authStatus == 'Iframe') && (
      <>
        <Divider />
        <Box sx={{
          width: openNav ? NAV_WIDTH : NAV_WIDTH_CLOSED, height: 50, display: 'flex', justifyContent: 'center', alignItems: 'center',
        }}
        >
          <Box
            component="img"
            src="/assets/images/logos/mindlabs-logo-dark.svg"
            sx={{
              position: 'absolute', maxHeight: '2.7vh', opacity: openNav ? 1 : 0, transition: openNav ? 'opacity 0.5s' : 'none', cursor: 'pointer', zIndex: 1,
            }}
          />
          <Box
            component="img"
            src="/assets/images/logos/mindlabs-icon.svg"
            sx={{
              maxHeight: '3vh', opacity: openNav ? 0 : 1, transition: openNav ? 'none' : 'opacity 0.3s', zIndex: 1,
            }}
          />
        </Box>
      </>
      )}
    </Scrollbar>
  );

  return (
    <Box
      component="nav"
      sx={{
        zIndex: 1300,
        flexShrink: { lg: 0 },
        width: { lg: NAV_WIDTH_CLOSED },
      }}
      onMouseEnter={() => { setOpenNav(true); }}
      onMouseLeave={isDesktop ? () => { setOpenNav(false); } : () => {}}
    >
      {(isDesktop) ? (
        <Drawer
          variant="permanent"
          PaperProps={{
            sx: {
              width: openNav ? NAV_WIDTH : NAV_WIDTH_CLOSED,
              bgcolor: '#fff',
              boxShadow: 1,
              borderRightStyle: 'dashed',
              transition: 'width 0.1s',
            },
          }}
        >
          {renderContent}
        </Drawer>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: {
              width: NAV_WIDTH,
              bgcolor: '#fff',
              boxShadow: 1,
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
